import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Home, RefreshCw, TrendingUp, Building2, ExternalLink, ArrowRight, 
  Users, Hammer, Briefcase, Smartphone, Calculator, 
  FileText, DollarSign, Globe, Stethoscope, Layers, Landmark, Key, Repeat, PiggyBank
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

// Translations for Mortgage page
const pageText = {
  en: {
    hero: {
      title: 'Mortgage Services',
      subtitle: 'Expert mortgage solutions for residential and commercial properties through Dominion Lending Centres Utmost Financial',
      cta: 'Visit DLC Utmost Financial'
    },
    purchaseRefinance: {
      title: 'Purchase & Refinance',
      subtitle: 'Core mortgage solutions for buying, renewing, or refinancing your property',
      purchase: { title: 'Purchase', desc: "Buying your first home or next property? We'll help you find the best rates and terms." },
      switch: { title: 'Switch or Renewal', desc: "Don't just sign at your current rate. Let us shop around to find you a better deal." },
      refinance: { title: 'Refinance', desc: 'Access your home equity for renovations, debt consolidation, or investments.' },
      spousal: { title: 'Spousal Buyout', desc: 'Navigate property division during separation with tailored mortgage solutions.' }
    },
    borrowerPrograms: {
      title: 'Programs by Borrower Type',
      subtitle: 'Tailored mortgage solutions designed for your unique situation',
      newToCanada: { title: 'New To Canada', desc: 'Mortgage solutions for newcomers, immigrants, and non-permanent residents.' },
      medical: { title: 'Medical Professionals', desc: 'Programs for doctors, dentists, and healthcare professionals.' },
      selfEmployed: { title: 'Self-Employed / Stated Income', desc: 'Options for business owners with non-traditional income documentation.' },
      alternative: { title: 'Alternative & Private Lending', desc: "Solutions for credit challenges or when traditional options aren't available." },
      insurerResources: 'Insurer Resources:'
    },
    equity: {
      title: 'Equity & Investment Products',
      subtitle: 'Leverage your home equity or invest in property',
      secondMortgage: { title: 'Second Mortgage', desc: 'Access home equity for renovations, debt consolidation, or investments.' },
      heloc: { title: 'HELOC', desc: 'Flexible revolving credit secured by your home equity.' },
      chip: { title: 'CHIP Reverse Mortgage', desc: "Access up to 55% of your home's value tax-free. No monthly payments required." },
      rental: { title: 'Rental Property', desc: 'Investment property financing for residential rental income properties.' }
    },
    improvement: {
      title: 'Purchase + Improvement Programs',
      subtitle: 'Finance both your property and renovation costs together',
      purchasePlus: { title: 'Purchase Plus Improvement', desc: 'Finance both your home purchase and renovation costs in one mortgage. Ideal for buying a fixer-upper or adding value to your new home.' },
      refinancePlus: { title: 'Refinance Plus Improvement', desc: 'Access equity and fund home improvements with a single refinance solution. Perfect for major renovations or home upgrades.' }
    },
    propertyTypes: {
      title: 'Property Types We Finance',
      subtitle: 'Comprehensive financing for residential and commercial properties',
      residentialTitle: 'Residential Properties',
      residentialItem1: 'Single-family homes',
      residentialItem2: 'Condominiums and townhouses',
      residentialItem3: 'Multi-family properties (up to 4 units)',
      residentialItem4: 'Vacation and second homes',
      commercialTitle: 'Commercial Properties',
      commercialItem1: 'Office buildings and retail spaces',
      commercialItem2: 'Industrial and warehouse properties',
      commercialItem3: 'Mixed-use developments',
      commercialItem4: 'Multi-family apartments (5+ units)'
    },
    insurers: {
      title: "Canada's Mortgage Default Insurers",
      cmhc: 'Canada Mortgage and Housing Corporation',
      sagen: "Canada's Leading Private Mortgage Insurer",
      canadaGuaranty: '100% Canadian-Owned Mortgage Insurer'
    },
    toolbox: {
      title: 'My Mortgage Toolbox',
      subtitle: 'Download our free app to access powerful mortgage calculators and planning tools.',
      scanText: 'Scan with your phone camera to download',
      affordability: { title: 'Affordability Calculator', desc: 'See how much you can borrow' },
      payment: { title: 'Payment Calculator', desc: 'Calculate monthly payments' },
      closing: { title: 'Closing Costs', desc: 'Estimate all your fees' },
      stress: { title: 'Stress Test Tool', desc: 'Test your qualification' },
      learnMore: 'Learn More'
    },
    cta: {
      title: 'Ready to Get Started?',
      subtitle: 'Connect with our mortgage professionals for expert advice and competitive rates',
      button1: 'Visit DLC Website',
      button2: 'Call 1-877-239-9060'
    }
  },
  fr: {
    hero: {
      title: 'Services Hypothécaires',
      subtitle: 'Solutions hypothécaires expertes pour propriétés résidentielles et commerciales via Dominion Lending Centres Utmost Financial',
      cta: 'Visiter DLC Utmost Financial'
    },
    purchaseRefinance: {
      title: 'Achat & Refinancement',
      subtitle: 'Solutions hypothécaires de base pour acheter, renouveler ou refinancer votre propriété',
      purchase: { title: 'Achat', desc: "Vous achetez votre première maison ou prochaine propriété? Nous vous aiderons à trouver les meilleurs taux et conditions." },
      switch: { title: 'Transfert ou Renouvellement', desc: "Ne signez pas simplement au taux actuel. Laissez-nous magasiner pour vous trouver une meilleure offre." },
      refinance: { title: 'Refinancement', desc: "Accédez à l'équité de votre maison pour des rénovations, consolidation de dettes ou investissements." },
      spousal: { title: 'Rachat Conjugal', desc: 'Naviguez la division de propriété lors d\'une séparation avec des solutions hypothécaires adaptées.' }
    },
    borrowerPrograms: {
      title: 'Programmes par Type d\'Emprunteur',
      subtitle: 'Solutions hypothécaires adaptées à votre situation unique',
      newToCanada: { title: 'Nouveaux au Canada', desc: 'Solutions hypothécaires pour nouveaux arrivants, immigrants et résidents non permanents.' },
      medical: { title: 'Professionnels de la Santé', desc: 'Programmes pour médecins, dentistes et professionnels de la santé.' },
      selfEmployed: { title: 'Travailleurs Autonomes / Revenu Déclaré', desc: 'Options pour propriétaires d\'entreprise avec documentation de revenu non traditionnelle.' },
      alternative: { title: 'Prêt Alternatif & Privé', desc: 'Solutions pour défis de crédit ou quand les options traditionnelles ne sont pas disponibles.' },
      insurerResources: 'Ressources Assureurs:'
    },
    equity: {
      title: 'Produits d\'Équité & Investissement',
      subtitle: 'Exploitez l\'équité de votre maison ou investissez dans l\'immobilier',
      secondMortgage: { title: 'Deuxième Hypothèque', desc: 'Accédez à l\'équité pour rénovations, consolidation de dettes ou investissements.' },
      heloc: { title: 'Marge de Crédit Hypothécaire', desc: 'Crédit renouvelable flexible garanti par l\'équité de votre maison.' },
      chip: { title: 'Hypothèque Inversée CHIP', desc: 'Accédez jusqu\'à 55% de la valeur de votre maison libre d\'impôt. Aucun paiement mensuel requis.' },
      rental: { title: 'Propriété Locative', desc: 'Financement de propriété d\'investissement pour revenus locatifs résidentiels.' }
    },
    improvement: {
      title: 'Programmes Achat + Amélioration',
      subtitle: 'Financez à la fois votre propriété et vos coûts de rénovation ensemble',
      purchasePlus: { title: 'Achat Plus Amélioration', desc: 'Financez l\'achat de votre maison et les coûts de rénovation en une seule hypothèque. Idéal pour acheter une propriété à rénover ou ajouter de la valeur.' },
      refinancePlus: { title: 'Refinancement Plus Amélioration', desc: 'Accédez à l\'équité et financez les améliorations avec une seule solution de refinancement. Parfait pour rénovations majeures.' }
    },
    propertyTypes: {
      title: 'Types de Propriétés que Nous Finançons',
      subtitle: 'Financement complet pour propriétés résidentielles et commerciales',
      residentialTitle: 'Propriétés Résidentielles',
      residentialItem1: 'Maisons unifamiliales',
      residentialItem2: 'Condos et maisons de ville',
      residentialItem3: 'Propriétés multi-logements (jusqu\'à 4 unités)',
      residentialItem4: 'Maisons de vacances et secondaires',
      commercialTitle: 'Propriétés Commerciales',
      commercialItem1: 'Immeubles de bureaux et espaces commerciaux',
      commercialItem2: 'Propriétés industrielles et entrepôts',
      commercialItem3: 'Développements à usage mixte',
      commercialItem4: 'Appartements multi-logements (5+ unités)'
    },
    insurers: {
      title: 'Assureurs Hypothécaires du Canada',
      cmhc: 'Société Canadienne d\'Hypothèques et de Logement',
      sagen: 'Principal Assureur Hypothécaire Privé du Canada',
      canadaGuaranty: 'Assureur Hypothécaire 100% Canadien'
    },
    toolbox: {
      title: 'My Mortgage Toolbox',
      subtitle: 'Téléchargez notre application gratuite pour accéder à des calculateurs hypothécaires puissants et outils de planification.',
      scanText: 'Scannez avec votre téléphone pour télécharger',
      affordability: { title: 'Calculateur d\'Accessibilité', desc: 'Voyez combien vous pouvez emprunter' },
      payment: { title: 'Calculateur de Paiement', desc: 'Calculez les paiements mensuels' },
      closing: { title: 'Frais de Clôture', desc: 'Estimez tous vos frais' },
      stress: { title: 'Test de Résistance', desc: 'Testez votre qualification' },
      learnMore: 'En Savoir Plus'
    },
    cta: {
      title: 'Prêt à Commencer?',
      subtitle: 'Connectez-vous avec nos professionnels hypothécaires pour des conseils experts et des taux compétitifs',
      button1: 'Visiter le Site DLC',
      button2: 'Appeler 1-877-239-9060'
    }
  }
};

const MortgagePage = () => {
  const { language } = useLanguage();
  const text = pageText[language];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden bg-[#0077B6]">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0077B6] to-[#005A8C]" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white">{text.hero.title}</h1>
          <p className="text-lg text-blue-100 mb-6">
            {text.hero.subtitle}
          </p>
          <Button
            asChild
            size="lg"
            className="bg-[#D4AF37] text-[#003D5C] hover:bg-[#B8962E] font-bold"
          >
            <a href="https://www.dlcutmostfinancial.ca" target="_blank" rel="noopener noreferrer">
              {text.hero.cta} <ExternalLink className="ml-2 h-4 w-4" />
            </a>
          </Button>
        </div>
      </section>

      {/* ========== SECTION 1: Purchase & Refinance (White Background) ========== */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-800 mb-3">{text.purchaseRefinance.title}</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">{text.purchaseRefinance.subtitle}</p>
            <div className="h-1 w-24 bg-[#D4AF37] mx-auto mt-4"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="bg-[#F0F7FF] border border-blue-100 hover:border-[#0077B6] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-14 h-14 rounded-xl bg-[#0077B6]/10 border-2 border-[#0077B6] flex items-center justify-center mb-4">
                  <Key className="h-7 w-7 text-[#0077B6]" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-800">{text.purchaseRefinance.purchase.title}</h3>
                <p className="text-gray-600 text-sm mb-3">{text.purchaseRefinance.purchase.desc}</p>
                <a href="https://www.cmhc-schl.gc.ca/professionals/project-funding-and-mortgage-financing/mortgage-loan-insurance/mortgage-loan-insurance-homeownership-programs/purchase" target="_blank" rel="noopener noreferrer" className="text-xs text-[#0077B6] hover:underline flex items-center">
                  <ExternalLink className="h-3 w-3 mr-1" />CMHC Purchase Program
                </a>
              </CardContent>
            </Card>

            <Card className="bg-[#F0F7FF] border border-blue-100 hover:border-[#0077B6] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-14 h-14 rounded-xl bg-[#0077B6]/10 border-2 border-[#0077B6] flex items-center justify-center mb-4">
                  <Repeat className="h-7 w-7 text-[#0077B6]" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-800">{text.purchaseRefinance.switch.title}</h3>
                <p className="text-gray-600 text-sm mb-3">{text.purchaseRefinance.switch.desc}</p>
                <a href="https://www.mcap.com/residential-mortgages/products/our-mortgages" target="_blank" rel="noopener noreferrer" className="text-xs text-[#0077B6] hover:underline flex items-center">
                  <ExternalLink className="h-3 w-3 mr-1" />MCAP Mortgage Options
                </a>
              </CardContent>
            </Card>

            <Card className="bg-[#F0F7FF] border border-blue-100 hover:border-[#0077B6] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-14 h-14 rounded-xl bg-[#0077B6]/10 border-2 border-[#0077B6] flex items-center justify-center mb-4">
                  <TrendingUp className="h-7 w-7 text-[#0077B6]" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-800">{text.purchaseRefinance.refinance.title}</h3>
                <p className="text-gray-600 text-sm mb-3">{text.purchaseRefinance.refinance.desc}</p>
                <a href="https://www.cmhc-schl.gc.ca/professionals/project-funding-and-mortgage-financing/mortgage-loan-insurance/mortgage-loan-insurance-homeownership-programs/refinance" target="_blank" rel="noopener noreferrer" className="text-xs text-[#0077B6] hover:underline flex items-center">
                  <ExternalLink className="h-3 w-3 mr-1" />CMHC Refinance Program
                </a>
              </CardContent>
            </Card>

            <Card className="bg-[#F0F7FF] border border-blue-100 hover:border-[#0077B6] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-14 h-14 rounded-xl bg-[#0077B6]/10 border-2 border-[#0077B6] flex items-center justify-center mb-4">
                  <Users className="h-7 w-7 text-[#0077B6]" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-800">{text.purchaseRefinance.spousal.title}</h3>
                <p className="text-gray-600 text-sm mb-3">{text.purchaseRefinance.spousal.desc}</p>
                <a href="https://www.firstnational.ca/mortgage-brokers/mortgage-solutions" target="_blank" rel="noopener noreferrer" className="text-xs text-[#0077B6] hover:underline flex items-center">
                  <ExternalLink className="h-3 w-3 mr-1" />First National Solutions
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ========== SECTION 2: Borrower-Specific Programs (Dark Background) ========== */}
      <section className="py-12 bg-[#003D5C]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-white mb-3">{text.borrowerPrograms.title}</h2>
            <p className="text-blue-200 max-w-2xl mx-auto">{text.borrowerPrograms.subtitle}</p>
            <div className="h-1 w-24 bg-[#D4AF37] mx-auto mt-4"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* New To Canada */}
            <Card className="bg-white/10 backdrop-blur border border-white/20 hover:border-[#D4AF37] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37] flex items-center justify-center mb-4">
                  <Globe className="h-6 w-6 text-[#003D5C]" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-white">{text.borrowerPrograms.newToCanada.title}</h3>
                <p className="text-blue-200 text-sm mb-4">{text.borrowerPrograms.newToCanada.desc}</p>
                <div className="space-y-1.5">
                  <p className="text-xs text-[#D4AF37] font-semibold uppercase">{text.borrowerPrograms.insurerResources}</p>
                  <a href="https://www.sagen.ca/products-and-services/new-to-canada/" target="_blank" rel="noopener noreferrer" className="flex items-center text-xs text-blue-100 hover:text-[#D4AF37]">
                    <ExternalLink className="h-3 w-3 mr-1.5" />Sagen New to Canada
                  </a>
                  <a href="https://www.cmhc-schl.gc.ca/professionals/project-funding-and-mortgage-financing/mortgage-loan-insurance/mortgage-loan-insurance-homeownership-programs/newcomers" target="_blank" rel="noopener noreferrer" className="flex items-center text-xs text-blue-100 hover:text-[#D4AF37]">
                    <ExternalLink className="h-3 w-3 mr-1.5" />CMHC Newcomers Program
                  </a>
                  <a href="https://www.canadaguaranty.ca/new-to-canada/" target="_blank" rel="noopener noreferrer" className="flex items-center text-xs text-blue-100 hover:text-[#D4AF37]">
                    <ExternalLink className="h-3 w-3 mr-1.5" />Canada Guaranty
                  </a>
                </div>
              </CardContent>
            </Card>

            {/* Medical Professionals */}
            <Card className="bg-white/10 backdrop-blur border border-white/20 hover:border-[#D4AF37] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37] flex items-center justify-center mb-4">
                  <Stethoscope className="h-6 w-6 text-[#003D5C]" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-white">{text.borrowerPrograms.medical.title}</h3>
                <p className="text-blue-200 text-sm mb-4">{text.borrowerPrograms.medical.desc}</p>
                <div className="space-y-1.5">
                  <p className="text-xs text-[#D4AF37] font-semibold uppercase">{text.borrowerPrograms.insurerResources}</p>
                  <a href="https://invested.mdm.ca/buying-a-home-during-residency/" target="_blank" rel="noopener noreferrer" className="flex items-center text-xs text-blue-100 hover:text-[#D4AF37]">
                    <ExternalLink className="h-3 w-3 mr-1.5" />MD Financial - Residents
                  </a>
                  <a href="https://www.firstnational.ca/mortgage-brokers/mortgage-solutions" target="_blank" rel="noopener noreferrer" className="flex items-center text-xs text-blue-100 hover:text-[#D4AF37]">
                    <ExternalLink className="h-3 w-3 mr-1.5" />First National Programs
                  </a>
                  <a href="https://www.mcap.com/residential-mortgages/products/our-mortgages" target="_blank" rel="noopener noreferrer" className="flex items-center text-xs text-blue-100 hover:text-[#D4AF37]">
                    <ExternalLink className="h-3 w-3 mr-1.5" />MCAP Products
                  </a>
                </div>
              </CardContent>
            </Card>

            {/* Self-Employed / Stated Income */}
            <Card className="bg-white/10 backdrop-blur border border-white/20 hover:border-[#D4AF37] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37] flex items-center justify-center mb-4">
                  <Briefcase className="h-6 w-6 text-[#003D5C]" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-white">{text.borrowerPrograms.selfEmployed.title}</h3>
                <p className="text-blue-200 text-sm mb-4">{text.borrowerPrograms.selfEmployed.desc}</p>
                <div className="space-y-1.5">
                  <p className="text-xs text-[#D4AF37] font-semibold uppercase">{text.borrowerPrograms.insurerResources}</p>
                  <a href="https://www.sagen.ca/products-and-services/business-for-self/" target="_blank" rel="noopener noreferrer" className="flex items-center text-xs text-blue-100 hover:text-[#D4AF37]">
                    <ExternalLink className="h-3 w-3 mr-1.5" />Sagen Business for Self
                  </a>
                  <a href="https://www.canadaguaranty.ca/low-doc-advantage/" target="_blank" rel="noopener noreferrer" className="flex items-center text-xs text-blue-100 hover:text-[#D4AF37]">
                    <ExternalLink className="h-3 w-3 mr-1.5" />Canada Guaranty Low Doc
                  </a>
                  <a href="https://www.cmhc-schl.gc.ca/professionals/project-funding-and-mortgage-financing/mortgage-loan-insurance/mortgage-loan-insurance-homeownership-programs/self-employed" target="_blank" rel="noopener noreferrer" className="flex items-center text-xs text-blue-100 hover:text-[#D4AF37]">
                    <ExternalLink className="h-3 w-3 mr-1.5" />CMHC Self-Employed
                  </a>
                </div>
              </CardContent>
            </Card>

            {/* Alternative & Private Lending */}
            <Card className="bg-white/10 backdrop-blur border border-white/20 hover:border-[#D4AF37] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37] flex items-center justify-center mb-4">
                  <DollarSign className="h-6 w-6 text-[#003D5C]" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-white">{text.borrowerPrograms.alternative.title}</h3>
                <p className="text-blue-200 text-sm mb-4">{text.borrowerPrograms.alternative.desc}</p>
                <div className="space-y-1.5">
                  <p className="text-xs text-[#D4AF37] font-semibold uppercase">{text.borrowerPrograms.insurerResources}</p>
                  <a href="https://www.firstnational.ca/mortgage-brokers/mortgage-solutions/accessing-alternative-mortgage-options-(excalibur)" target="_blank" rel="noopener noreferrer" className="flex items-center text-xs text-blue-100 hover:text-[#D4AF37]">
                    <ExternalLink className="h-3 w-3 mr-1.5" />First National Excalibur
                  </a>
                  <a href="https://www.equitablebank.ca/residential" target="_blank" rel="noopener noreferrer" className="flex items-center text-xs text-blue-100 hover:text-[#D4AF37]">
                    <ExternalLink className="h-3 w-3 mr-1.5" />Equitable Bank
                  </a>
                  <a href="https://www.mcap.com/residential-mortgages" target="_blank" rel="noopener noreferrer" className="flex items-center text-xs text-blue-100 hover:text-[#D4AF37]">
                    <ExternalLink className="h-3 w-3 mr-1.5" />MCAP Alternative
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ========== SECTION 3: Equity & Investment Products (White Background) ========== */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-800 mb-3">{text.equity.title}</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">{text.equity.subtitle}</p>
            <div className="h-1 w-24 bg-[#D4AF37] mx-auto mt-4"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="bg-[#F0F7FF] border border-blue-100 hover:border-[#0077B6] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-14 h-14 rounded-xl bg-[#0077B6]/10 border-2 border-[#0077B6] flex items-center justify-center mb-4">
                  <Layers className="h-7 w-7 text-[#0077B6]" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-800">{text.equity.secondMortgage.title}</h3>
                <p className="text-gray-600 text-sm mb-3">{text.equity.secondMortgage.desc}</p>
                <a href="https://www.sagen.ca/products-and-services/second-mortgage/" target="_blank" rel="noopener noreferrer" className="text-xs text-[#0077B6] hover:underline flex items-center">
                  <ExternalLink className="h-3 w-3 mr-1" />Sagen Second Mortgage
                </a>
              </CardContent>
            </Card>

            <Card className="bg-[#F0F7FF] border border-blue-100 hover:border-[#0077B6] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-14 h-14 rounded-xl bg-[#0077B6]/10 border-2 border-[#0077B6] flex items-center justify-center mb-4">
                  <Landmark className="h-7 w-7 text-[#0077B6]" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-800">{text.equity.heloc.title}</h3>
                <p className="text-gray-600 text-sm mb-3">{text.equity.heloc.desc}</p>
                <a href="https://www.canada.ca/en/financial-consumer-agency/services/mortgages/home-equity-line-credit.html" target="_blank" rel="noopener noreferrer" className="text-xs text-[#0077B6] hover:underline flex items-center">
                  <ExternalLink className="h-3 w-3 mr-1" />HELOC Guide (FCAC)
                </a>
              </CardContent>
            </Card>

            <Card className="bg-[#F0F7FF] border border-blue-100 hover:border-[#0077B6] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-14 h-14 rounded-xl bg-[#0077B6]/10 border-2 border-[#0077B6] flex items-center justify-center mb-4">
                  <PiggyBank className="h-7 w-7 text-[#0077B6]" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-800">{text.equity.chip.title}</h3>
                <p className="text-gray-600 text-sm mb-3">{text.equity.chip.desc}</p>
                <a href="https://www.homeequitybank.ca/products/chip-reverse-mortgage/" target="_blank" rel="noopener noreferrer" className="text-xs text-[#0077B6] hover:underline flex items-center">
                  <ExternalLink className="h-3 w-3 mr-1" />HomeEquity Bank CHIP
                </a>
              </CardContent>
            </Card>

            <Card className="bg-[#F0F7FF] border border-blue-100 hover:border-[#0077B6] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-14 h-14 rounded-xl bg-[#0077B6]/10 border-2 border-[#0077B6] flex items-center justify-center mb-4">
                  <Building2 className="h-7 w-7 text-[#0077B6]" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-800">{text.equity.rental.title}</h3>
                <p className="text-gray-600 text-sm mb-3">{text.equity.rental.desc}</p>
                <a href="https://www.firstnational.ca/mortgage-brokers/mortgage-solutions/buying-a-rental-or-investment-property" target="_blank" rel="noopener noreferrer" className="text-xs text-[#0077B6] hover:underline flex items-center">
                  <ExternalLink className="h-3 w-3 mr-1" />First National Rental
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ========== SECTION 4: Purchase + Improvements (Dark Background) ========== */}
      <section className="py-12 bg-[#003D5C]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-white mb-3">{text.improvement.title}</h2>
            <p className="text-blue-200 max-w-2xl mx-auto">{text.improvement.subtitle}</p>
            <div className="h-1 w-24 bg-[#D4AF37] mx-auto mt-4"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <Card className="bg-white/10 backdrop-blur border border-white/20 hover:border-[#D4AF37] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37] flex items-center justify-center mb-4">
                  <Hammer className="h-6 w-6 text-[#003D5C]" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-white">{text.improvement.purchasePlus.title}</h3>
                <p className="text-blue-200 text-sm mb-4">{text.improvement.purchasePlus.desc}</p>
                <div className="space-y-1.5">
                  <a href="https://www.cmhc-schl.gc.ca/professionals/project-funding-and-mortgage-financing/mortgage-loan-insurance/mortgage-loan-insurance-homeownership-programs/improvement" target="_blank" rel="noopener noreferrer" className="flex items-center text-xs text-blue-100 hover:text-[#D4AF37]">
                    <ExternalLink className="h-3 w-3 mr-1.5" />CMHC Improvement Program
                  </a>
                  <a href="https://www.sagen.ca/products-and-services/purchase-plus-improvements/" target="_blank" rel="noopener noreferrer" className="flex items-center text-xs text-blue-100 hover:text-[#D4AF37]">
                    <ExternalLink className="h-3 w-3 mr-1.5" />Sagen Purchase Plus
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur border border-white/20 hover:border-[#D4AF37] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37] flex items-center justify-center mb-4">
                  <Hammer className="h-6 w-6 text-[#003D5C]" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-white">{text.improvement.refinancePlus.title}</h3>
                <p className="text-blue-200 text-sm mb-4">{text.improvement.refinancePlus.desc}</p>
                <div className="space-y-1.5">
                  <a href="https://www.cmhc-schl.gc.ca/professionals/project-funding-and-mortgage-financing/mortgage-loan-insurance/mortgage-loan-insurance-homeownership-programs/refinance" target="_blank" rel="noopener noreferrer" className="flex items-center text-xs text-blue-100 hover:text-[#D4AF37]">
                    <ExternalLink className="h-3 w-3 mr-1.5" />CMHC Refinance Program
                  </a>
                  <a href="https://www.equitablebank.ca/residential" target="_blank" rel="noopener noreferrer" className="flex items-center text-xs text-blue-100 hover:text-[#D4AF37]">
                    <ExternalLink className="h-3 w-3 mr-1.5" />Equitable Bank Residential
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ========== SECTION 5: Property Types (White Background) ========== */}
      <section className="py-12 bg-[#F0F7FF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-800 mb-3">{text.propertyTypes.title}</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">{text.propertyTypes.subtitle}</p>
            <div className="h-1 w-24 bg-[#D4AF37] mx-auto mt-4"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Card className="bg-white border border-gray-200 hover:border-[#0077B6] transition-all hover:shadow-lg">
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-xl bg-[#0077B6]/10 border-2 border-[#0077B6] flex items-center justify-center mb-4">
                  <Home className="h-8 w-8 text-[#0077B6]" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-3">{text.propertyTypes.residentialTitle}</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-center"><ArrowRight className="h-4 w-4 text-[#D4AF37] mr-2" />{text.propertyTypes.residentialItem1}</li>
                  <li className="flex items-center"><ArrowRight className="h-4 w-4 text-[#D4AF37] mr-2" />{text.propertyTypes.residentialItem2}</li>
                  <li className="flex items-center"><ArrowRight className="h-4 w-4 text-[#D4AF37] mr-2" />{text.propertyTypes.residentialItem3}</li>
                  <li className="flex items-center"><ArrowRight className="h-4 w-4 text-[#D4AF37] mr-2" />{text.propertyTypes.residentialItem4}</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-white border border-gray-200 hover:border-[#0077B6] transition-all hover:shadow-lg">
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-xl bg-[#D4AF37]/10 border-2 border-[#D4AF37] flex items-center justify-center mb-4">
                  <Building2 className="h-8 w-8 text-[#D4AF37]" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-3">{text.propertyTypes.commercialTitle}</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-center"><ArrowRight className="h-4 w-4 text-[#D4AF37] mr-2" />{text.propertyTypes.commercialItem1}</li>
                  <li className="flex items-center"><ArrowRight className="h-4 w-4 text-[#D4AF37] mr-2" />{text.propertyTypes.commercialItem2}</li>
                  <li className="flex items-center"><ArrowRight className="h-4 w-4 text-[#D4AF37] mr-2" />{text.propertyTypes.commercialItem3}</li>
                  <li className="flex items-center"><ArrowRight className="h-4 w-4 text-[#D4AF37] mr-2" />{text.propertyTypes.commercialItem4}</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ========== Mortgage Insurers Reference ========== */}
      <section className="py-10 bg-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h3 className="text-xl font-bold text-gray-800 mb-6 text-center">{text.insurers.title}</h3>
          <div className="grid md:grid-cols-3 gap-6 text-center">
            <a href="https://www.cmhc-schl.gc.ca" target="_blank" rel="noopener noreferrer" className="p-4 bg-white rounded-lg shadow hover:shadow-md transition-shadow">
              <p className="font-bold text-[#0077B6]">CMHC</p>
              <p className="text-gray-500 text-sm">{text.insurers.cmhc}</p>
            </a>
            <a href="https://www.sagen.ca" target="_blank" rel="noopener noreferrer" className="p-4 bg-white rounded-lg shadow hover:shadow-md transition-shadow">
              <p className="font-bold text-[#0077B6]">Sagen</p>
              <p className="text-gray-500 text-sm">{text.insurers.sagen}</p>
            </a>
            <a href="https://www.canadaguaranty.ca" target="_blank" rel="noopener noreferrer" className="p-4 bg-white rounded-lg shadow hover:shadow-md transition-shadow">
              <p className="font-bold text-[#0077B6]">Canada Guaranty</p>
              <p className="text-gray-500 text-sm">{text.insurers.canadaGuaranty}</p>
            </a>
          </div>
        </div>
      </section>

      {/* ========== CTA Section ========== */}
      <section className="py-12 bg-[#0077B6]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-3">{text.cta.title}</h2>
          <p className="text-blue-100 mb-6 text-lg">
            {text.cta.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-[#D4AF37] text-[#003D5C] hover:bg-[#B8962E] font-bold">
              <a href="https://www.dlcutmostfinancial.ca" target="_blank" rel="noopener noreferrer">
                {text.cta.button1} <ExternalLink className="ml-2 h-4 w-4" />
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#0077B6] font-bold">
              <a href="tel:18772399060">{text.cta.button2}</a>
            </Button>
          </div>
        </div>
      </section>

      {/* ========== My Mortgage Toolbox ========== */}
      <section className="py-12 bg-gradient-to-r from-[#003D5C] to-[#005A8C]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="flex flex-col items-center">
              <a href="https://www.dlcapp.ca" target="_blank" rel="noopener noreferrer" className="bg-white p-4 rounded-2xl shadow-2xl hover:shadow-3xl transition-shadow cursor-pointer">
                <img src="https://customer-assets.emergentagent.com/job_finance-portal-213/artifacts/p75yxq80_IMG_4717.png" alt="My Mortgage Toolbox" className="w-64 h-64 md:w-72 md:h-72 object-contain" />
              </a>
              <p className="text-blue-200 mt-4 text-center text-sm">{text.toolbox.scanText}</p>
            </div>

            <div className="text-white">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-14 h-14 rounded-xl bg-[#D4AF37] flex items-center justify-center">
                  <Smartphone className="h-7 w-7 text-[#003D5C]" />
                </div>
                <h2 className="text-3xl font-bold">{text.toolbox.title}</h2>
              </div>
              <p className="text-blue-100 text-lg mb-6">{text.toolbox.subtitle}</p>
              
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="flex items-start gap-3">
                  <Calculator className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">{text.toolbox.affordability.title}</p>
                    <p className="text-blue-200 text-sm">{text.toolbox.affordability.desc}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <DollarSign className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">{text.toolbox.payment.title}</p>
                    <p className="text-blue-200 text-sm">{text.toolbox.payment.desc}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <FileText className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">{text.toolbox.closing.title}</p>
                    <p className="text-blue-200 text-sm">{text.toolbox.closing.desc}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <TrendingUp className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">{text.toolbox.stress.title}</p>
                    <p className="text-blue-200 text-sm">{text.toolbox.stress.desc}</p>
                  </div>
                </div>
              </div>

              <Button asChild size="lg" className="bg-[#D4AF37] text-[#003D5C] hover:bg-[#B8962E] font-bold">
                <a href="https://www.dlcapp.ca" target="_blank" rel="noopener noreferrer">
                  {text.toolbox.learnMore} <ExternalLink className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MortgagePage;
